/**
 * A custom element that handles free gift selection and adding to cart.
 * Displays a progress bar and product selector when cart total exceeds threshold.
 * Automatically removes free gift when cart drops below threshold.
 *
 * @extends {HTMLElement}
 */
class FreeGiftSelector extends HTMLElement {
  constructor() {
    super();
    this.isRemovingFreeGift = false;
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleCarouselPrev = this.handleCarouselPrev.bind(this);
    this.handleCarouselNext = this.handleCarouselNext.bind(this);
    this.updateCarouselButtons = this.updateCarouselButtons.bind(this);
  }

  connectedCallback() {
    this.attachEventListeners();
    this.initCarousel();
    this.initializeVariantSelectors();
    document.addEventListener('theme:cart:change', this.handleCartUpdate);
    document.addEventListener('theme:cart:add', this.handleCartUpdate);
    document.addEventListener('theme:cart:refresh', this.handleCartUpdate);
    document.addEventListener('theme:cart:update', this.handleCartUpdate);
    
    setTimeout(() => {
      this.handleCartUpdate({ type: 'initial', detail: {} });
      this.initCarousel();
      this.initializeVariantSelectors();
    }, 100);
  }

  /**
   * Initializes variant selectors - ensures add to cart buttons are disabled when "Select variant" is selected
   */
  initializeVariantSelectors() {
    const variantSelects = this.querySelectorAll('[data-variant-select]');
    variantSelects.forEach((select) => {
      const productCard = select.closest('.free-gift-selector__product-card');
      if (!productCard) return;

      const addToCartButton = productCard.querySelector('.free-gift-selector__add-to-cart');
      if (!addToCartButton) return;

      if (!select.value || select.value === '') {
        addToCartButton.disabled = true;
      }
    });
  }

  /**
   * Called when the element is removed from the DOM.
   * Cleans up event listeners to prevent memory leaks.
   */
  disconnectedCallback() {
    document.removeEventListener('theme:cart:change', this.handleCartUpdate);
    document.removeEventListener('theme:cart:add', this.handleCartUpdate);
    document.removeEventListener('theme:cart:refresh', this.handleCartUpdate);
    document.removeEventListener('theme:cart:update', this.handleCartUpdate);
  }

 
  attachEventListeners() {
    const variantSelects = this.querySelectorAll('[data-variant-select]');
    variantSelects.forEach((select) => {
      select.removeEventListener('change', this.handleVariantChange);
      select.addEventListener('change', this.handleVariantChange);
    });

    const addToCartButtons = this.querySelectorAll('.free-gift-selector__add-to-cart');
    addToCartButtons.forEach((button) => {
      button.removeEventListener('click', this.handleAddToCart);
      button.addEventListener('click', this.handleAddToCart);
    });

    const prevButton = this.querySelector('[data-carousel-prev]');
    const nextButton = this.querySelector('[data-carousel-next]');
    if (prevButton) {
      prevButton.removeEventListener('click', this.handleCarouselPrev);
      prevButton.addEventListener('click', this.handleCarouselPrev);
    }
    if (nextButton) {
      nextButton.removeEventListener('click', this.handleCarouselNext);
      nextButton.addEventListener('click', this.handleCarouselNext);
    }
  }

  /**
   * Handles variant selection change
   */
  handleVariantChange = (event) => {
    const select = event.target;
    const productCard = select.closest('.free-gift-selector__product-card');
    if (!productCard) return;

    const selectedVariantId = select.value;
    const selectedOption = select.options[select.selectedIndex];
    const addToCartButton = productCard.querySelector('.free-gift-selector__add-to-cart');
    const priceContainer = productCard.querySelector('.free-gift-selector__product-price');

    if (!addToCartButton) return;

    if (!selectedVariantId) {
      addToCartButton.disabled = true;
      return;
    }

    addToCartButton.dataset.variantId = selectedVariantId;
    addToCartButton.disabled = false;

    if (priceContainer && selectedOption) {
      priceContainer.innerHTML = `<span class="free-gift-selector__price">Free</span>`;
    }
  };

  /**
   * Handles add to cart button click
   */
  handleAddToCart = async (event) => {
    const button = event.currentTarget;
    if (!button || button.disabled) return;

    const productCard = button.closest('.free-gift-selector__product-card');
    if (!productCard) return;

    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;
    const sectionId = this.dataset.sectionId;

    if (!productId || !variantId) {
      const variantSelect = productCard.querySelector('[data-variant-select]');
      if (variantSelect && variantSelect.value) {
        button.dataset.variantId = variantSelect.value;
        return this.handleAddToCart(event);
      }
      return;
    }

    const allButtons = this.querySelectorAll('.free-gift-selector__add-to-cart');
    allButtons.forEach((btn) => {
      if (btn instanceof HTMLButtonElement) {
        btn.disabled = true;
      }
    });

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      if (sectionId) {
        formData.append('sections', sectionId);
      }

      const response = await fetch(`${theme.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status) {
        allButtons.forEach((btn) => {
          if (btn instanceof HTMLButtonElement) {
            btn.disabled = false;
          }
        });
        return;
      }

      this.hideSelector();

      document.dispatchEvent(
        new CustomEvent('theme:cart:add', {
          detail: {
            sections: data.sections || {},
          },
        })
      );

      document.dispatchEvent(
        new CustomEvent('theme:cart:refresh', {
          detail: {
            sections: data.sections || {},
          },
        })
      );

      setTimeout(() => {
        this.attachEventListeners();
      }, 100);
    } catch (error) {
      allButtons.forEach((btn) => {
        if (btn instanceof HTMLButtonElement) {
          btn.disabled = false;
        }
      });
    }
  };

  /**
   * Initializes the carousel if there are more than 2 products
   */
  initCarousel() {
    const wrapper = this.querySelector('.free-gift-selector__carousel-wrapper');
    if (!wrapper) return;
    
    this.updateCarouselButtons();
    
    wrapper.addEventListener('scroll', () => {
      this.updateCarouselButtons();
    });
  }

  /**
   * Handles previous button click for carousel
   */
  handleCarouselPrev = () => {
    const wrapper = this.querySelector('.free-gift-selector__carousel-wrapper');
    if (!wrapper) return;

    const isHorizontalDesktop = this.dataset.horizontalDesktop === 'true';
    const isHorizontalMobile = this.dataset.horizontalMobile === 'true';
    const isMobile = window.innerWidth <= 479;
    const isHorizontal = (isMobile && isHorizontalMobile) || (!isMobile && isHorizontalDesktop);
    
    const cardWidth = isHorizontal ? 320 : 160;
    const scrollAmount = cardWidth + 10;
    
    wrapper.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    });
  };

  /**
   * Handles next button click for carousel
   */
  handleCarouselNext = () => {
    const wrapper = this.querySelector('.free-gift-selector__carousel-wrapper');
    if (!wrapper) return;

    // Check if horizontal layout is enabled (desktop or mobile)
    const isHorizontalDesktop = this.dataset.horizontalDesktop === 'true';
    const isHorizontalMobile = this.dataset.horizontalMobile === 'true';
    const isMobile = window.innerWidth <= 479;
    const isHorizontal = (isMobile && isHorizontalMobile) || (!isMobile && isHorizontalDesktop);
    
    const cardWidth = isHorizontal ? 320 : 160;
    const scrollAmount = cardWidth + 10; 
    
    wrapper.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  };

  /**
   * Updates carousel navigation button states based on scroll position
   */
  updateCarouselButtons() {
    const wrapper = this.querySelector('.free-gift-selector__carousel-wrapper');
    const prevButton = this.querySelector('[data-carousel-prev]');
    const nextButton = this.querySelector('[data-carousel-next]');

    if (!wrapper) return;

    const { scrollLeft, scrollWidth, clientWidth } = wrapper;
    const isAtStart = scrollLeft <= 0;
    const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1;

    if (prevButton) {
      prevButton.disabled = isAtStart;
    }
    if (nextButton) {
      nextButton.disabled = isAtEnd;
    }
  }


  /**
   * Handles cart update events and manages selector visibility.
   * Fetches cart total from API, checks for free gifts, and updates UI accordingly.
   * Removes free gift if cart drops below threshold.
   *
   * @param {Event} event - The cart update event
   */
  handleCartUpdate = async (event) => {
    if (this.isRemovingFreeGift) {
      return;
    }

    const threshold = parseInt(this.dataset.threshold || '0', 10);
    const sectionId = this.dataset.sectionId;
    const giftProductIds = this.dataset.giftProductIds?.split(',').map(id => id.trim()).filter(Boolean) || [];

    if (!sectionId || threshold === 0) {
      return;
    }

    let hasFreeGiftInCart = this.checkForFreeGiftInCart(giftProductIds);
    let freeGiftLineIndex = null;

    if (hasFreeGiftInCart) {
      freeGiftLineIndex = this.getFreeGiftLineIndex(giftProductIds);
      
      if (!freeGiftLineIndex) {
        freeGiftLineIndex = await this.getFreeGiftLineIndexFromAPI(giftProductIds);
      }
    }

    await new Promise(resolve => setTimeout(resolve, 100));

    let cartTotal = 0;
    let attempts = 0;
    const maxAttempts = 2;
    let lastCartTotal = 0;
    
    while (attempts < maxAttempts) {
      if (attempts > 0) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      try {
        const cartResponse = await fetch(`${theme.routes.root}cart.js?t=${Date.now()}`);
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          const apiTotal = cart.total_price || 0;
          
          if (apiTotal === lastCartTotal && apiTotal > 0) {
            cartTotal = apiTotal;
            this.dataset.cartTotal = cartTotal.toString();
            break;
          }
          
          if (attempts === maxAttempts - 1) {
            cartTotal = apiTotal;
            this.dataset.cartTotal = cartTotal.toString();
            break;
          }
          
          lastCartTotal = apiTotal;
        }
      } catch (error) {
      }
      
      attempts++;
    }

    if (threshold > 0 && cartTotal < threshold && hasFreeGiftInCart) {
      if (!freeGiftLineIndex) {
        freeGiftLineIndex = await this.getFreeGiftLineIndexFromAPI(giftProductIds);
      }
      
      if (freeGiftLineIndex) {
        this.isRemovingFreeGift = true;
        try {
          await this.removeFreeGift(freeGiftLineIndex, sectionId);
          hasFreeGiftInCart = false;
        } catch (error) {
        } finally {
          setTimeout(() => {
            this.isRemovingFreeGift = false;
          }, 500);
        }
      }
    }

    if (!hasFreeGiftInCart) {
      hasFreeGiftInCart = await this.checkForFreeGiftInCartAPI(giftProductIds);
    }

    const hasProducts = this.querySelector('.free-gift-selector__products') !== null;
    if (!hasProducts || this.innerHTML.trim().length === 0) {
      await this.refreshSection();
    }

    if (hasFreeGiftInCart) {
      this.hideSelector();
    } else if (threshold > 0) {
      if (cartTotal === 0) {
        const domCartTotal = parseInt(this.dataset.cartTotal || '0', 10);
        if (domCartTotal > 0) {
          cartTotal = domCartTotal;
        }
      }
      this.showSelector();
      this.updateProgressBar(cartTotal);
      requestAnimationFrame(() => {
        this.attachEventListeners();
        setTimeout(() => {
          this.initCarousel();
        }, 100);
      });
    } else {
      this.hideSelector();
    }
  };

  /**
   * Checks if a free gift product is currently in the cart by examining the DOM.
   *
   * @param {string[]} giftProductIds - Array of product IDs that qualify as free gifts
   * @returns {boolean} True if a free gift is found in the cart
   */
  checkForFreeGiftInCart(giftProductIds) {
    if (!giftProductIds || giftProductIds.length === 0) return false;

    const cartItemsComponent = document.querySelector('cart-items');
    if (!cartItemsComponent) {
      return false;
    }

    const currentRows = cartItemsComponent.querySelectorAll('.cart__item');
    for (const row of currentRows) {
      const isFreeGift = row.getAttribute('data-free-gift') === 'true';
      if (isFreeGift) return true;
    }

    return false;
  }

  /**
   * Checks if a free gift product is in the cart by querying the cart API.
   * Used as a fallback when DOM check fails.
   *
   * @param {string[]} giftProductIds - Array of product IDs that qualify as free gifts
   * @returns {Promise<boolean>} True if a free gift is found in the cart
   */
  async checkForFreeGiftInCartAPI(giftProductIds) {
    if (!giftProductIds || giftProductIds.length === 0) return false;
    
    try {
      const cartResponse = await fetch(`${theme.routes.root}cart.js?t=${Date.now()}`);
      if (!cartResponse.ok) return false;
      
      const cart = await cartResponse.json();
      if (!cart.items || cart.items.length === 0) return false;
      
      for (const item of cart.items) {
        const productId = item.product_id.toString();
        if (giftProductIds.includes(productId)) {
          return true;
        }
      }
    } catch (error) {
    }
    
    return false;
  }

  /**
   * Gets the line index of a free gift product from the cart DOM.
   *
   * @param {string[]} giftProductIds - Array of product IDs that qualify as free gifts
   * @returns {number|null} The line index (1-based) or null if not found
   */
  getFreeGiftLineIndex(giftProductIds) {
    const cartItemsComponent = document.querySelector('cart-items');
    if (!cartItemsComponent) return null;

    const currentRows = cartItemsComponent.querySelectorAll('.cart__item');
    for (const row of currentRows) {
      const isFreeGift = row.getAttribute('data-free-gift') === 'true';
      if (isFreeGift) {
        const itemIndex = row.getAttribute('data-item-index');
        if (itemIndex) {
          return parseInt(itemIndex, 10);
        }
      }

      const itemTitle = row.getAttribute('data-item-title');
      const itemKey = row.getAttribute('data-item');
      
      const productLink = row.querySelector('a[href*="/products/"]');
      if (productLink) {
        const href = productLink.getAttribute('href');
        const productHandleMatch = href.match(/\/products\/([^\/\?]+)/);
        if (productHandleMatch) {
        }
      }
    }

    return null;
  }

  /**
   * Gets the line index of a free gift product from the cart API.
   * Used as a fallback when DOM check fails.
   *
   * @param {string[]} giftProductIds - Array of product IDs that qualify as free gifts
   * @returns {Promise<number|null>} The line index (1-based) or null if not found
   */
  async getFreeGiftLineIndexFromAPI(giftProductIds) {
    try {
      const cartResponse = await fetch(`${theme.routes.root}cart.js?t=${Date.now()}`);
      if (!cartResponse.ok) return null;
      
      const cart = await cartResponse.json();
      if (!cart.items || cart.items.length === 0) return null;
      
      for (let i = 0; i < cart.items.length; i++) {
        const item = cart.items[i];
        const productId = item.product_id.toString();
        if (giftProductIds.includes(productId)) {
          return i + 1;
        }
      }
    } catch (error) {
    }
    
    return null;
  }

  /**
   * Removes a free gift product from the cart by setting its quantity to 0.
   *
   * @param {number} lineIndex - The 1-based line index of the free gift item
   * @param {string} sectionId - The section ID to update after removal
   * @throws {Error} If the removal request fails
   */
  removeFreeGift = async (lineIndex, sectionId) => {
    try {
      const sectionsToUpdate = new Set([sectionId]);
      
      const cartDrawerSection = document.querySelector('[data-section-type="cart-drawer"]');
      if (cartDrawerSection) {
        const cartDrawerSectionId = cartDrawerSection.getAttribute('data-section-id');
        if (cartDrawerSectionId) {
          sectionsToUpdate.add(cartDrawerSectionId);
        }
      }

      sectionsToUpdate.add('api-cart-items');

      document.querySelectorAll('cart-items').forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        line: lineIndex,
        quantity: 0,
        sections: Array.from(sectionsToUpdate).join(','),
      });

      const response = await fetch(`${theme.routes.cart_change_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: body,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(data.errors);
      }

      document.dispatchEvent(
        new CustomEvent('theme:cart:refresh', {
          detail: {
            sections: data.sections || {},
          },
        })
      );

      document.dispatchEvent(
        new CustomEvent('theme:cart:update', {
          detail: {
            sections: data.sections || {},
          },
        })
      );
    } catch (error) {
      throw error;
    }
  };

  /**
   * Hides the free gift selector and its child elements.
   */
  hideSelector() {
    this.style.display = 'none';
    const header = this.querySelector('.free-gift-selector__header');
    const products = this.querySelector('.free-gift-selector__products');
    if (header instanceof HTMLElement) header.style.display = 'none';
    if (products instanceof HTMLElement) products.style.display = 'none';
  }

  /**
   * Shows the free gift selector with forced visibility styles.
   * Ensures progress bar, header, and products are visible.
   */
  showSelector() {
    this.removeAttribute('style');
    
    this.style.setProperty('display', 'block', 'important');
    this.style.setProperty('visibility', 'visible', 'important');
    this.style.setProperty('opacity', '1', 'important');
    
    this.classList.remove('free-gift-selector--hidden');
    
    const header = this.querySelector('.free-gift-selector__header');
    const products = this.querySelector('.free-gift-selector__products');
    const progressBar = this.querySelector('.free-gift-selector__progress-bar');
    
    if (header instanceof HTMLElement) {
      header.style.setProperty('display', '', 'important');
      header.style.setProperty('visibility', 'visible', 'important');
    }
    if (products instanceof HTMLElement) {
      products.style.setProperty('display', '', 'important');
      products.style.setProperty('visibility', 'visible', 'important');
    }
    if (progressBar instanceof HTMLElement) {
      progressBar.style.setProperty('display', '', 'important');
    }
  }

  /**
   * Updates the progress bar and message based on cart total vs threshold.
   * Shows progress when below threshold, shows products when above threshold.
   * @param {number} [cartTotalParam] - The cart total in cents. If not provided, reads from dataset.
   */
  updateProgressBar(cartTotalParam) {
    const threshold = parseInt(this.dataset.threshold || '0', 10);
    const cartTotal = cartTotalParam !== undefined ? cartTotalParam : parseInt(this.dataset.cartTotal || '0', 10);

    if (threshold === 0) return;

    const progressBar = this.querySelector('.free-gift-selector__progress-bar');
    const progressFill = this.querySelector('.free-gift-selector__progress-fill');
    const progressMessage = this.querySelector('.free-gift-selector__progress-message');
    const header = this.querySelector('.free-gift-selector__header');
    const products = this.querySelector('.free-gift-selector__products');

    if (progressBar instanceof HTMLElement) {
      progressBar.style.display = '';
    }

    if (cartTotal < threshold) {
      if (header instanceof HTMLElement) header.style.display = 'none';
      if (products instanceof HTMLElement) products.style.display = 'none';

      const progressPercentage = Math.min((cartTotal / threshold) * 100, 100);
      const remaining = threshold - cartTotal;

      if (progressFill instanceof HTMLElement) {
        progressFill.style.setProperty('--progress-percentage', `${progressPercentage}%`);
      }

      if (progressMessage instanceof HTMLElement) {
        const formattedAmount = this.formatMoney(remaining);
        progressMessage.textContent = `Spend ${formattedAmount} more to get a free gift`;
      }
    } else {
      if (header instanceof HTMLElement) header.style.display = '';
      if (products instanceof HTMLElement) products.style.display = '';

      if (progressFill instanceof HTMLElement) {
        progressFill.style.setProperty('--progress-percentage', '100%');
      }

      if (progressMessage instanceof HTMLElement) {
        progressMessage.style.display = 'none';
      }
    }
  }

  /**
   * Refreshes the selector's HTML content by fetching the section from the server.
   * Used to restore products if they're missing from the DOM.
   */
  refreshSection = async () => {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      return;
    }

    try {
      const sectionUrl = `${theme.routes.root}?section_id=${sectionId}`;
      const response = await fetch(sectionUrl);
      if (!response.ok) {
        return;
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const newSelector = doc.querySelector('free-gift-selector');
      
      if (!newSelector) {
        return;
      }

      const updatedCartTotal = newSelector.getAttribute('data-cart-total');
      if (updatedCartTotal) {
        this.dataset.cartTotal = updatedCartTotal;
      }

      const currentThreshold = this.dataset.threshold;
      const currentSectionId = this.dataset.sectionId;
      const currentGiftProductIds = this.dataset.giftProductIds;
      
      const newContent = newSelector.innerHTML.trim();
      
      if (newContent.length > 0) {
        this.innerHTML = newContent;
      }

      this.setAttribute('data-threshold', currentThreshold);
      this.setAttribute('data-section-id', currentSectionId);
      this.setAttribute('data-gift-product-ids', currentGiftProductIds);
      if (updatedCartTotal) {
        this.setAttribute('data-cart-total', updatedCartTotal);
      }

      this.attachEventListeners();
      setTimeout(() => {
        this.initCarousel();
      }, 100);
    } catch (error) {
    }
  };

  /**
   * Formats a money amount in cents to a localized currency string.
   *
   * @param {number} cents - The amount in cents
   * @returns {string} Formatted currency string using theme's money format
   */
  formatMoney(cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents, theme.moneyFormat);
    }
    const amount = cents / 100;
    const formatted = amount.toFixed(2);
    return `$${formatted}`;
  }
}

if (!customElements.get('free-gift-selector')) {
  customElements.define('free-gift-selector', FreeGiftSelector);
}
